import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import { Search } from "lucide-react";

const TABS = [
  { slug: null, label: "All" },
  { slug: "ai", label: "AI" },
  { slug: "real-estate", label: "Real Estate" },
  { slug: "insurance", label: "Insurance" },
  { slug: "loans", label: "Loans" },
];
const DURATIONS = [
  { v: null, label: "Any duration" },
  { v: 3, label: "3 hours" },
  { v: 9, label: "9 hours" },
  { v: 36, label: "36 hours" },
];

export default function Courses() {
  const { industryOrSlug } = useParams();
  const { location } = useApp();
  const [industry, setIndustry] = useState(industryOrSlug || null);
  const [duration, setDuration] = useState(null);
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ courses: [], currency: null });

  useEffect(() => { setIndustry(industryOrSlug || null); }, [industryOrSlug]);

  useEffect(() => {
    if (!location) return;
    api.courses(industry, location.country).then(setData);
  }, [industry, location]);

  const sym = data.currency?.symbol || location?.symbol || "₹";
  const filtered = data.courses.filter((c) =>
    (duration ? c.duration_hours === duration : true) &&
    (query ? c.title.toLowerCase().includes(query.toLowerCase()) : true)
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="courses-page">
      <div className="asf-overline mb-3">Course catalogue</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Courses</h1>
      <p className="text-slate-500 mt-3">Practical, industry-focused programs with certificates. Prices shown in {sym === "₹" ? "INR" : "USD"}.</p>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mt-8">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t.label} data-testid={`filter-industry-${t.slug || "all"}`}
              onClick={() => setIndustry(t.slug)}
              className={`px-4 py-2 rounded-full text-sm border transition-colors ${industry === t.slug ? "bg-asf-orange border-asf-orange text-white" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="md:ml-auto flex gap-3">
          <select data-testid="filter-duration" value={duration || ""} onChange={(e) => setDuration(e.target.value ? Number(e.target.value) : null)}
            className="rounded-full bg-asf-app border border-slate-200 px-4 py-2 text-sm outline-none focus:border-asf-orange">
            {DURATIONS.map((d) => <option key={d.label} value={d.v || ""}>{d.label}</option>)}
          </select>
          <div className="relative">
            <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="course-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search courses"
              className="rounded-full bg-asf-app border border-slate-200 pl-9 pr-4 py-2 text-sm outline-none focus:border-asf-orange" />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
        {filtered.map((c) => <CourseCard key={c.slug} course={c} symbol={sym} />)}
      </div>
      {filtered.length === 0 && <p className="text-slate-500 mt-10">No courses match your filters.</p>}
    </div>
  );
}
